"use strict"
var inherits = require('util').inherits
var _ = require('lodash')
var Counter = require('./Counter')

function _add (a,b) {
  return a + b
}

var DEFAULT_SORT = 'nameAsc'

var DEFAULT_VIEW_SETTINGS = {
  sortMethod: DEFAULT_SORT, // set data sort method and direction
  rangeFilter: [0, 100], // filter by passed percentage-range (e.g. [0,60]) of ordered bandwidth data
  modeFilter: 'all', // (all|packet|head|netflow|none) filter by passed mode string (fp/sh/nf/dp)
  estimateCapture: true,  // (bool, Protocol mode only) true=estimate capture based on selected captureProfile
  collapseSingles: true, // (bool, IP mode only) prevent single-ip children in nested subnet tree (bubble to the top instead)
  flattenList: false, // (bool, IP mode only) prevent recursive subnet nesting

  averageCompressionRatio: 0.7,
  defaultMode: 'packet',
  exceptions: null
}

var VALID_VIEW_SETTINGS = Object.keys(DEFAULT_VIEW_SETTINGS)

function getValidViewSettings (viewOptions) {
  return _.pick(viewOptions, VALID_VIEW_SETTINGS)
}

var SORT_METHODS = {
  bytesDesc: function (itm) { return -itm.bytes },
  bytesAsc: function (itm) { return itm.bytes },
  nameDesc: function (itm) { return -itm.name.charCodeAt() },
  nameAsc: function (itm) { return itm.name }
}

var _currentSortMethod = SORT_METHODS[DEFAULT_SORT]  //.bytesDesc

var _modeGroupings = {
  netflow: [],
  head: [],
  packet: []
}

function _getTickTotal (tick) {
  return _.reduce(tick, function (tickTotal, val, key) {
    if (key.indexOf("bytes.protocol.") !== -1) {
      return tickTotal + val
    } else {
      return tickTotal
    }
  }, 0)
}

function dataTransform (data) {
  var self = this
  var _history = data.history
  var _viewSettings = this._viewSettings

  _currentSortMethod = SORT_METHODS[_viewSettings.sortMethod] || SORT_METHODS[DEFAULT_SORT]

  if (_viewSettings.exceptions) {
    if (_viewSettings.estimateCapture) {
      // Compute most current modeGroupings for estimate.
      _modeGroupings = {
        none: [],
        netflow: [],
        head: [],
        packet: []
      }

      var protos = this._protocols //_(protocolFamilies).pluck('protocols').flatten().value()

      for (var i = 0, iLen = protos.length; i < iLen; i++) {
        var proto = protos[i]
        var exc = _.find(_viewSettings.exceptions, {'protocol_id': proto.id})
        if (exc) {
          _modeGroupings[exc.mode].push({
            name: proto.name,
            dataKey: "bytes.protocol." + proto.name.toLowerCase()
          })
        } else {
          // Default mode
          _modeGroupings[_viewSettings.defaultMode].push({
            name: proto.name,
            dataKey: "bytes.protocol." + proto.name.toLowerCase()
          })
        }
      }

      // Replace history data with estimates based on currently selected capture profile
      _history = _.reduce(data.history, function (output, tick) {
        var newTick = _.clone(tick)
        // For each data tick, loop through all protocols
        // 1: Full Packet protocols only need simulated compression
        // If proto.mode is 'packet', all bytes would be captured, retain byte count
        for (var p = 0, pLen = _modeGroupings.packet.length; p < pLen; p++) {
          var pProto = _modeGroupings.packet[p]
          newTick[pProto.dataKey] = Math.round((tick[pProto.dataKey] || 0) * _viewSettings.averageCompressionRatio)
        }
        // 2:Zero out all Netflow protocols
        // If mode is 'netflow', no bytes would be captured, zero this tick
        // for nProto in _modeGroupings.netflow
        for (var n = 0, nLen = _modeGroupings.netflow.length; n < nLen; n++) {
          var nProto = _modeGroupings.netflow[n]
          newTick[nProto.dataKey] = 0
        }
        // 3:Also zero out all None protocols
        // If mode is 'netflow', no bytes will be captured, zero this tick
        // for nProto in _modeGroupings.none
        for (var d = 0, dLen = _modeGroupings.none.length; d < dLen; d++) {
          var dProto = _modeGroupings.none[d]
          newTick[dProto.dataKey] = 0
        }
        // 3: Calculate all Stream Head protocols
        // If mode is 'head', then use totals fomr the `bytes_head` counters, which indicate
        // how many bytes would have been captured for the given timeframe in Stream Head mode
        for (var h = 0, hLen = _modeGroupings.head.length; h < hLen; h++) {
          var hProto = _modeGroupings.head[h]
          var tickKey = "bytes_head.protocol." + hProto.name.toLowerCase()
          newTick[hProto.dataKey] = Math.round((tick[tickKey] || 0) * _viewSettings.averageCompressionRatio)
        }
        // Update Tick Total with newly computed value
        newTick.total = _getTickTotal(newTick)
        output.push(newTick)
        return output
      }, [])
    } else {
      // need to update each tick's history total to only include bytes (not netflows/byte_head used for estimation)
      _history = _.map(data.history, function (tick) {
        tick.total = _getTickTotal(tick)
        return tick
      })
    }
  }

  var historyTotal = 0
  var _protocolsToFamilies = {} // Protocol name to family name mapping
  var familiesWithTraffic = _.reduce(this._protocolFamilies, function (output, fam) {
    var famTotal = 0
    var famNameFormatted = fam.name.trim().replace(/\s+/g, "").replace(/\//g, "_").toLowerCase()
    var famColor = self._protocolFamilyIdToColor[fam.id]

    var protosWithTraffic = _.map(fam.protocols, function (proto) {
      proto._id = fam.id + "_proto_" + proto.id // Uniqueify the ID so we can differentiate it from numeric familyIDs
      proto.exceptionVal = proto.id // Retain pointer to very conflicty numerical protocolId
      proto.dataKey = "bytes.protocol." + proto.name.toLowerCase()
      proto.bytes = _.reduce(_history, function (sum, tick) {
        return sum + _.get(tick, proto.dataKey, 0)
      }, 0)
      proto.max = _.max(_history, proto.dataKey)[proto.dataKey]
      proto.isFamily = false
      proto.familyName = famNameFormatted
      proto.end = true
      proto.depth = 2
      proto.filtered = false
      proto.color = famColor

      if (_viewSettings.exceptions) {
        var exc = _.find(_viewSettings.exceptions, {'protocol_id': proto.id})
        if (exc) {
          proto.mode = exc.mode
          proto.exceptionMode = exc.mode
          proto.exception_id = exc.id
        } else {
          proto.mode = _viewSettings.defaultMode //'default'
          proto.exceptionMode = 'default'
          proto.exception_id = null
        }
      }
      var protoKey = "bytes_protocol_" + proto.name.toLowerCase()
      _protocolsToFamilies[protoKey] = famNameFormatted //fam.name.trim().replace(/\s+/g, "").replace(/\//g, '_').toLowerCase()
      famTotal += proto.bytes
      return proto
    })

    historyTotal += famTotal

    fam._id = "_fam_" + fam.id
    fam.modeCounts = _.countBy(protosWithTraffic, 'exceptionMode')
    fam.bytes = famTotal
    fam.familyName = famNameFormatted
    fam.isFamily = true
    fam.exceptionVal = fam.id
    fam.end = false
    fam.depth = 1
    fam.color = famColor
    fam.protocols = _.sortBy(protosWithTraffic, _currentSortMethod)
    fam.filtered = false
    fam.protocolsSortedBytes = _.sortBy(protosWithTraffic, SORT_METHODS.bytesAsc) // Needed to ensure protos are sorted by bytes WITHIN their family

    output.push(fam)
    return output
  }, [])

  var _families = _.sortBy(familiesWithTraffic, _currentSortMethod)

  // Update protocol stack order for the timeline
  // NOTE this list intentionally ignores the user-set sort order.
  // For the purposes of filtering, the list must be sorted by bytes
  var sortedProtos = _(familiesWithTraffic)
    .sortBy(SORT_METHODS.bytesAsc)
    .pluck('protocolsSortedBytes')
    .flatten()
    .reject({bytes: 0})
    .valueOf()

  // filter by capture mode
  if (_viewSettings.modeFilter !== 'all') {
    sortedProtos = _.filter(sortedProtos, {'mode': _viewSettings.modeFilter})
  }

  // filter by bandwidth range last, so it can operate on the final selection range
  if (_viewSettings.rangeFilter != null && !_.isEqual(_viewSettings.rangeFilter, [0, 100])) {
    var maxIndex = sortedProtos.length
    var startIndex = Math.floor(maxIndex * (_viewSettings.rangeFilter[0] / 100))
    var endIndex = Math.ceil(maxIndex * (_viewSettings.rangeFilter[1] / 100))
    sortedProtos = sortedProtos.slice(startIndex, endIndex)
  }

  var sortedProtoKeys = _.pluck(sortedProtos, 'dataKey')

  // Strip filtered protocols for the family packed data,
  // and flag appropriate families/protocols with their filtered state
  var _packData = _.reduce(_families, function (out, family) {
    family.children = _.reduce(family.protocols, function (out2, proto) {
      var _filtered = sortedProtoKeys.indexOf(proto.dataKey) === -1
      proto.filtered = _filtered // Mutate the original source proto
      if (!_filtered) {
        out2.push(proto)
      }
      return out2
    }, [])
    if (family.children.length > 0) {
      out.push( family )
    } else {
      family.filtered = true
    }
    return out
  }, [])

  // Recaluclate max value based on the now-final filtered keys
  data.maxValue = _.reduce(_history, function (prevMax, nextTick) {
    var tickMax = _.reduce(_.values(_.pick(nextTick, sortedProtoKeys)), _add, 0)
    return tickMax > prevMax ? tickMax : prevMax
  }, 0)

  data.history = _history // stack data for timeline, filtered to only include keys with values across the time span. May be live or estimated based on mode
  data.families = _families // contains a sorted list of all families and protocols with aggregate history totals attached (drives profiler list pane)
  data.sortedProtocols = sortedProtos // contains a sorted and filtered list of proto keys (drives stack order of timeline)
  data.sortedKeys = sortedProtoKeys
  data.totalBytes = historyTotal
  data.protocolsToFamilies = _protocolsToFamilies
  data.packedData = {
    name: '',
    isRoot: true,
    bytes: historyTotal,
    children: _packData,
    depth: 0
  }

  return _.assign(data, _viewSettings)
}


/**
 * Graph Constructor / setup
 */
function ProfilerProtocolGraph (graphOptions, viewOptions) {
  if (!(this instanceof ProfilerProtocolGraph)) {
    return new ProfilerProtocolGraph(graphOptions, viewOptions)
  }
  this._protocolFamilies = _.reject(viewOptions.protocolFamilies, function (family) {
    return family.protocols.length === 0
  })
  this._protocols = viewOptions.protocols
  this._protocolFamilyIdToColor = viewOptions.protocolFamilyIdToColor
  this._viewSettings =  _.defaults(getValidViewSettings(viewOptions), DEFAULT_VIEW_SETTINGS)

  // Initialize inherited Counter graph functions
  Counter.call(this, graphOptions, viewOptions)
}

inherits(ProfilerProtocolGraph, Counter)

var proto = ProfilerProtocolGraph.prototype

proto.getGraphData = function () {
  var counterGraphData = Counter.prototype.getGraphData.call(this)
  // FIXME: in an effort to transition to summary counters
  // which come back in a crazy way, we must deal with both here.
  var protos = this._protocols
  counterGraphData.history = _.map(counterGraphData.history, function (tick) {
    return _.mapKeys(tick, function (value, key) {
      if (key.indexOf('0,0,0,0,0,0,'/*p,pid*/) === 0) {
        // turn 8 bytes into an int code golf session
        // eslint-disable-next-line
        var pid = key.split(',').reduce(function(v,b){return v<<8|b})
        var proto = _.find(protos, {id: pid})
        return "bytes.protocol." + (proto ? proto.name.toLowerCase() : pid)
      }
      return key
    })
  })
  return dataTransform.call(this, counterGraphData)
}

proto.setViewOptions = function (partialViewOptions) {
  // Merge in the new partial view options
  this._viewSettings = _.assign(this._viewSettings, getValidViewSettings(partialViewOptions))
}

module.exports = ProfilerProtocolGraph
